import { useState, useEffect } from 'react'
import { useTranslation } from '../i18n/LanguageContext'
import { useAuth } from '../contexts/AuthContext'
import { users, activities as activitiesApi, type UserStats, type ActivityRow } from '../api/client'
import { useMobile } from '../hooks/useMobile'
import { findAnySavedActivity, clearActivitySession } from '../hooks/useActivitySession'
import StatCard from './StatCard'
import Footer from './Footer'


interface DashboardProps {
  onSelectGame:           (game: string) => void
  onGoToSubscription:     () => void
  onGoToDownloads?:       () => void
  onGoToProfessional?:    () => void
  onGoToStats?:           () => void
}

// Visual-only metadata — no tier logic here
const GAME_META: Record<string, { emoji: string; color: string; accent: string }> = {
  memory:       { emoji: '🃏', color: '#2D6A4F', accent: '#B7E4C7' },
  wordmatch:    { emoji: '📖', color: '#6B4C3B', accent: '#F0E4A8' },
  cutlery:      { emoji: '🍴', color: '#7A4F2D', accent: '#F0CC6A' },
  wordsearch:   { emoji: '🔍', color: '#1D6FA4', accent: '#C0E4F8' },
  sudoku:       { emoji: '🔢', color: '#5C3D2E', accent: '#EDE0CC' },
  maths:        { emoji: '🧮', color: '#1A4A7A', accent: '#BADDF5' },
  shoppinglist: { emoji: '🛒', color: '#2D6A4F', accent: '#D4EDDA' },
  eurmoney:     { emoji: '🪙', color: '#8B6914', accent: '#F5E6B0' },
  pillbox:      { emoji: '💊', color: '#5C3069', accent: '#EDD5F5' },
  attention:    { emoji: '🔤', color: '#1A5276', accent: '#D6EAF8' },
  symmetry:      { emoji: '🪞', color: '#2D4A7A', accent: '#D0DCF0' },
  whats_missing: { emoji: '🔎', color: '#6A2D6A', accent: '#F0D5F5' },
  roman:         { emoji: '🏛️', color: '#5C3D2E', accent: '#F0E4CC' },
  crosswords:    { emoji: '📝', color: '#1D6E5E', accent: '#C8EDE6' },
  hours:         { emoji: '🕐', color: '#1A4A7A', accent: '#C8DCF5' },
  synonyms:      { emoji: '📚', color: '#2D5E3A', accent: '#C5E8D0' },
  puzzle:        { emoji: '🧩', color: '#4A2D6A', accent: '#E8D5F5' },
  chess:         { emoji: '♟', color: '#1a3a5c', accent: '#d4e6f1' },
  numtarget:     { emoji: '🎯', color: '#1A4A7A', accent: '#C8DCF5' },
}

const TIER_LABELS: Record<string, string> = { BASIC: 'Básico', PLUS: 'Plus', PREMIUM: 'Premium' }

const STAT_META = {
  today:  { icon: '☀️', accentColor: '#52B788' },
  streak: { icon: '🔥', accentColor: '#D4A017' },
  best:   { icon: '⭐', accentColor: '#B5541A' },
}


export default function Dashboard({ onSelectGame, onGoToSubscription, onGoToDownloads, onGoToProfessional, onGoToStats }: DashboardProps) {
  const { t, language } = useTranslation()
  const { user, logout } = useAuth()
  const isMobile = useMobile()
  const [stats,        setStats]        = useState<UserStats | null>(null)
  const [activityRows, setActivityRows] = useState<ActivityRow[]>([])
  const [resumeSlug,   setResumeSlug]   = useState<string | null>(() => findAnySavedActivity()?.slug ?? null)

  useEffect(() => {
    users.stats().catch(() => null).then((s) => { if (s) setStats(s) })
    activitiesApi.list().then(setActivityRows).catch(() => {})
  }, [])

  return (
    <div style={styles.root}>
      {/* ── Header ── */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.brandRow}>
            <span style={styles.brandLeaf}>🌿</span>
            <span style={styles.brandName}>{t.app.name}</span>
          </div>
          <p style={styles.tagline}>{t.app.tagline}</p>
        </div>
        {/* User info + logout */}
        <div style={styles.headerUser}>
          <button
            onClick={() => onSelectGame('profile')}
            style={styles.profileBtn}
            aria-label={t.profile.title}
          >
            <span style={styles.profileAvatar}>
              {(user?.displayName ?? user?.email ?? '?')[0].toUpperCase()}
            </span>
            {user?.displayName && (
              <span style={styles.headerUserName}>{user.displayName}</span>
            )}
          </button>
          {user?.role === 'ADMIN' && (
            <button
              onClick={() => onSelectGame('admin')}
              style={styles.adminBtn}
              aria-label="Admin"
            >
              Admin
            </button>
          )}
          <button
            onClick={logout}
            style={styles.logoutBtn}
            aria-label={t.auth.logout}
          >
            {t.auth.logout}
          </button>
        </div>
        <div style={styles.headerDecor} aria-hidden />
      </header>

      <main style={styles.main}>
        {/* ── Stats ── */}
        <section style={styles.statsRow} aria-label="Progreso">
          <div style={styles.statsGrid}>
              {(Object.entries(STAT_META) as [keyof typeof STAT_META, typeof STAT_META[keyof typeof STAT_META]][]).map(
                ([key, meta]) => {
                  const rawVal = stats?.[key]
                  const display = rawVal === null || rawVal === undefined ? '—'
                    : key === 'best' ? rawVal.toLocaleString(language === 'es' ? 'es-ES' : 'en-US')
                    : String(rawVal)
                  const label = t.dashboard.stats[key]
                  return (
                    <StatCard
                      key={key}
                      icon={meta.icon}
                      value={display}
                      label={label}
                      accentColor={meta.accentColor}
                      onClick={onGoToStats}
                    />
                  )
                }
              )}
            </div>
        </section>

        {/* ── Resume banner ── */}
        {resumeSlug && (() => {
          const copy = (t.dashboard.games as Record<string, { label: string }>)[resumeSlug]
          if (!copy) return null
          return (
            <div style={styles.resumeCard} role="region" aria-label={t.common.resumeTitle}>
              <div style={styles.resumeText}>
                <span style={styles.resumeIcon}>▶</span>
                <div>
                  <div style={styles.resumeTitle}>{t.common.resumeTitle}</div>
                  <div style={styles.resumeActivity}>{copy.label}</div>
                </div>
              </div>
              <div style={styles.resumeActions}>
                <button
                  style={styles.resumeBtnPrimary}
                  onClick={() => onSelectGame(resumeSlug)}
                >
                  {t.common.sessionResumeContinue}
                </button>
                <button
                  style={styles.resumeBtnGhost}
                  onClick={() => { clearActivitySession(resumeSlug); setResumeSlug(null) }}
                >
                  {t.common.sessionResumeNew}
                </button>
              </div>
            </div>
          )
        })()}

        {/* ── Section title ── */}
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>{t.dashboard.activitiesTitle}</h2>
          <div style={styles.sectionLine} />
        </div>

        {/* ── Downloads + Professional mode shortcuts ── */}
        {(onGoToDownloads || (onGoToProfessional && user?.tier === 'PREMIUM')) && (
          <div style={{
            display:       'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap:           12,
            marginBottom:  16,
          }}>
            {onGoToDownloads && (
              <button
                onClick={onGoToDownloads}
                style={{ ...styles.downloadsBtn, flex: 1, marginBottom: 0 }}
                aria-label={t.dashboard.downloadsAriaLabel}
              >
                <span>🖨️</span>
                <span>{t.dashboard.downloadsBtnText}</span>
                <span style={{ marginLeft: 'auto', opacity: 0.6 }}>→</span>
              </button>
            )}
            {onGoToProfessional && user?.tier === 'PREMIUM' && (
              <button
                onClick={onGoToProfessional}
                style={{ ...styles.downloadsBtn, flex: 1, marginBottom: 0 }}
                aria-label={t.professional.hubTitle}
              >
                <span>🩺</span>
                <span>{t.professional.hubTitle}</span>
                <span style={{ marginLeft: 'auto', opacity: 0.6 }}>→</span>
              </button>
            )}
          </div>
        )}

        {/* ── Games Grid ── */}
        <section style={styles.gamesGrid} aria-label={t.dashboard.activitiesGrid}>
          {activityRows.map((row, i) => {
            const meta = GAME_META[row.slug]
            if (!meta) return null  // unknown slug — no visual config, skip
            const copy = (t.dashboard.games as Record<string, { label: string; description: string }>)[row.slug]
            if (!copy) return null  // no i18n entry yet, skip
            const lockedLabel = row.locked ? `🔒 ${TIER_LABELS[row.requiredTier] ?? row.requiredTier}` : ''
            return (
              <GameCard
                key={row.slug}
                emoji={meta.emoji}
                color={meta.color}
                accent={meta.accent}
                available={!row.locked}
                label={copy.label}
                description={copy.description}
                lockedLabel={lockedLabel}
                index={i}
                onSelect={() => !row.locked ? onSelectGame(row.slug) : onGoToSubscription()}
              />
            )
          })}
        </section>
      </main>

      <Footer />
    </div>
  )
}

interface GameCardProps {
  emoji: string
  color: string
  accent: string
  available: boolean
  label: string
  description: string
  lockedLabel: string
  index: number
  onSelect: () => void
}

function GameCard({ emoji, color, accent, available, label, description, lockedLabel, index, onSelect }: GameCardProps) {
  return (
    <button
      className="fade-up"
      onClick={onSelect}
      aria-label={available ? label : `${label} — ${lockedLabel}`}
      style={{
        ...styles.gameCard,
        animationDelay: `${0.2 + index * 0.09}s`,
        opacity: available ? 1 : 0.72,
        cursor: 'pointer',
      }}
    >
      <div style={{ ...styles.gameIconWrap, background: accent }}>
        <span style={styles.gameIcon}>{emoji}</span>
      </div>

      <div style={styles.gameInfo}>
        <div style={styles.gameTitleRow}>
          <h3 style={styles.gameTitle}>{label}</h3>
          {!available && (
            <span style={styles.comingSoonBadge}>{lockedLabel}</span>
          )}
        </div>
        <p style={styles.gameDescription}>{description}</p>
      </div>

      <div style={{ ...styles.playArrow, background: available ? color : '#C0AFA0' }}>
        <span style={styles.playArrowIcon}>{available ? '→' : '🔒'}</span>
      </div>
    </button>
  )
}

/* ── Styles ──────────────────────────────────────────────────── */
const styles: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    position: 'relative',
    background: 'linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)',
    padding: '40px 32px 56px',
    overflow: 'hidden',
  },
  headerInner: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 960,
    margin: '0 auto',
  },
  headerUser: {
    position: 'absolute',
    bottom: 20,
    right: 100, // leaves room for the lang toggle
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerUserName: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.7)',
  },
  profileBtn: {
    display:     'flex',
    alignItems:  'center',
    gap:         8,
    background:  'rgba(255,255,255,0.10)',
    border:      '1px solid rgba(255,255,255,0.20)',
    borderRadius: 10,
    padding:     '5px 12px 5px 5px',
    cursor:      'pointer',
    transition:  'background 0.15s ease',
    minHeight:   36,
  },
  profileAvatar: {
    width:          28,
    height:         28,
    borderRadius:   '50%',
    background:     'rgba(255,255,255,0.2)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontSize: '0.875rem',
    fontWeight:     800,
    color:          '#FFFFFF',
    flexShrink:     0,
  },
  adminBtn: {
    fontSize: '0.875rem',
    fontWeight: 800,
    color: '#D4A017',
    background: 'rgba(212,160,23,0.15)',
    border: '1px solid rgba(212,160,23,0.40)',
    borderRadius: 8,
    padding: '7px 14px',
    cursor: 'pointer',
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    minHeight: 36,
  },
  logoutBtn: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: 'rgba(255,255,255,0.65)',
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.20)',
    borderRadius: 8,
    padding: '7px 14px',
    cursor: 'pointer',
    letterSpacing: '0.03em',
    transition: 'background 0.15s ease',
    minHeight: 36,
  },
  headerDecor: {
    position: 'absolute',
    top: -60,
    right: -60,
    width: 300,
    height: 300,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
    pointerEvents: 'none',
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  brandLeaf: { fontSize: '2.2rem' },
  brandName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '2.6rem',
    fontWeight: 700,
    color: '#FFFFFF',
    letterSpacing: '-0.02em',
    lineHeight: 1,
  },
  tagline: {
    fontFamily: 'Nunito, sans-serif',
    fontSize: '1.1rem',
    color: 'rgba(255,255,255,0.75)',
    fontWeight: 500,
    letterSpacing: '0.01em',
  },

  main: {
    flex: 1,
    maxWidth: 960,
    width: '100%',
    margin: '0 auto',
    padding: '32px 24px 48px',
  },

  statsRow: {
    gap: 16,
    marginBottom: 40,
    flexWrap: 'wrap' as const,
  },
  statCard: {
    flex: '1 1 120px',
    background: '#FFFFFF',
    borderRadius: 16,
    padding: '20px 16px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 2px 12px rgba(27,67,50,0.08)',
    border: '1.5px solid #EFE7D7',
  },
  statsGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap:                 14,
  },
  resumeCard: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    gap:            16,
    background:     '#F0FAF4',
    border:         '1.5px solid #B7E4C7',
    borderRadius:   16,
    padding:        '16px 20px',
    marginBottom:   20,
    flexWrap:       'wrap' as const,
  },
  resumeText: {
    display:    'flex',
    alignItems: 'center',
    gap:        12,
    flex:       1,
    minWidth:   0,
  },
  resumeIcon: {
    fontSize:   '1.4rem',
    color:      '#1B4332',
    flexShrink: 0,
  },
  resumeTitle: {
    fontSize: '0.875rem',
    color:      '#4A7C59',
    fontWeight: 600,
  },
  resumeActivity: {
    fontSize:   '1.1rem',
    fontWeight: 800,
    color:      '#1B4332',
    marginTop:  2,
  },
  resumeActions: {
    display:    'flex',
    gap:        8,
    flexShrink: 0,
  },
  resumeBtnPrimary: {
    padding:      '12px 20px',
    fontSize:     '0.95rem',
    fontWeight:   700,
    color:        '#FFFFFF',
    background:   'linear-gradient(135deg, #1B4332, #2D6A4F)',
    borderRadius: 10,
    minHeight:    48,
    whiteSpace:   'nowrap' as const,
  },
  resumeBtnGhost: {
    padding:      '12px 16px',
    fontSize:     '0.9rem',
    fontWeight:   600,
    color:        '#6B4C3B',
    background:   'transparent',
    border:       '1.5px solid #D8C9B8',
    borderRadius: 10,
    minHeight:    48,
    whiteSpace:   'nowrap' as const,
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
  },
  sectionTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.9rem',
    fontWeight: 600,
    color: '#1B4332',
    whiteSpace: 'nowrap' as const,
  },
  sectionLine: {
    flex: 1,
    height: 1.5,
    background: 'linear-gradient(90deg, #D8C9B8, transparent)',
    borderRadius: 2,
  },

  downloadsBtn: {
    display:       'flex',
    alignItems:    'center',
    gap:           10,
    width:         '100%',
    background:    '#FFFFFF',
    border:        '1.5px solid #E8DDD0',
    borderRadius:  12,
    padding:       '14px 18px',
    cursor:        'pointer',
    fontSize:      '1rem',
    fontWeight:    600,
    color:         '#1B4332',
    marginBottom:  16,
    minHeight:     52,
    textAlign:     'left' as const,
    boxShadow:     '0 1px 4px rgba(0,0,0,0.05)',
  },

  gamesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 20,
  },

  gameCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    background: '#FFFFFF',
    borderRadius: 20,
    padding: '24px 20px',
    boxShadow: '0 2px 12px rgba(27,67,50,0.07)',
    border: '1.5px solid #EFE7D7',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
    textAlign: 'left' as const,
    width: '100%',
  },
  gameIconWrap: {
    width: 64,
    height: 64,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  gameIcon: { fontSize: '2rem' },
  gameInfo: { flex: 1, minWidth: 0 },
  gameTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 6,
  },
  gameTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#2E1F14',
    lineHeight: 1.1,
  },
  comingSoonBadge: {
    fontSize: '0.875rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: '#9A8070',
    background: '#EFE7D7',
    padding: '3px 8px',
    borderRadius: 20,
  },
  gameDescription: {
    fontSize: '0.9rem',
    color: '#6B4C3B',
    lineHeight: 1.5,
    fontWeight: 500,
  },
  playArrow: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  playArrowIcon: {
    color: '#FFFFFF',
    fontSize: '1.1rem',
    lineHeight: 1,
  },

}
